import is from '@sindresorhus/is';
import type { FunctionComponent, HTMLAttributes, JSX, KeyboardEvent, ReactNode } from 'react';
import { useState } from 'react';

import { Popover, PopoverTrigger } from '../popover/Popover';
import { ScrollArea } from '../scroll-area/ScrollArea';
import {
    Empty,
    IconWrapper,
    ItemContent,
    ItemLabel,
    List,
    StyledCheckIcon,
    StyledItem,
    StyledPopoverContent,
    Wrapper,
} from './dropdown-styles';
import type { MultiSelectProps, SingleSelectProps } from './dropdown-utils';
import { handleSelect } from './dropdown-utils';
import { useDropdownKeyboard } from './useDropdownKeyboard';

export type DropdownOption<T = string> = {
    readonly disabled?: boolean;
    readonly icon?: ReactNode;
    readonly label: string;
    readonly value: T;
};

type BaseDropdownProps<T> = {
    readonly checkmarkColor?: string;
    readonly children?: ReactNode;
    readonly disabled?: boolean;
    readonly emptyMessage?: string;
    readonly onOpenChange?: (open: boolean) => void;
    readonly open?: boolean;
    readonly options: readonly DropdownOption<T>[];
};

type SingleSelectDropdownProps<T = string> = BaseDropdownProps<T> & SingleSelectProps<T>;

type MultiSelectDropdownProps<T = string> = BaseDropdownProps<T> & MultiSelectProps<T>;

type DropdownProps<T = string> = MultiSelectDropdownProps<T> | SingleSelectDropdownProps<T>;

export type DropdownItemProps = HTMLAttributes<HTMLDivElement> & {
    readonly checkmarkColor?: string;
    readonly children: ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly icon?: ReactNode;
    readonly isActive?: boolean;
    readonly isSelected?: boolean;
    readonly onSelect?: (value: string) => void;
    readonly value?: string;
};

export const DropdownItem: FunctionComponent<DropdownItemProps> = ({
    checkmarkColor,
    children,
    className = '',
    disabled = false,
    icon,
    isActive = false,
    isSelected = false,
    onSelect,
    value = '',
    ...props
}) => {
    const handleSelect = (): void => {
        if (disabled) return;
        onSelect?.(value);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect();
        }
    };

    return (
        <StyledItem
            aria-selected={isSelected}
            className={className}
            data-active={isActive}
            disabled={disabled}
            role="option"
            tabIndex={disabled ? -1 : 0}
            onClick={handleSelect}
            onKeyDown={handleKeyDown}
            {...props}
        >
            <ItemContent>
                {!is.undefined(icon) && <IconWrapper>{icon}</IconWrapper>}
                <ItemLabel>{children}</ItemLabel>
            </ItemContent>
            {isSelected && <StyledCheckIcon $color={checkmarkColor} />}
        </StyledItem>
    );
};

export const Dropdown = <T extends string = string>(props: DropdownProps<T>): JSX.Element => {
    const {
        checkmarkColor,
        children,
        disabled = false,
        emptyMessage = 'No results found.',
        onOpenChange,
        open: controlledOpen,
        options,
        value,
    } = props;

    const [internalOpen, setInternalOpen] = useState(false);
    const [cursor, setCursor] = useState(0);

    const open = controlledOpen ?? internalOpen;
    const setOpen = onOpenChange ?? setInternalOpen;

    const { keyDownListener } = useDropdownKeyboard({
        cursor,
        isOpen: open,
        onSelect: (value) => handleSelect(props, value),
        onSetCursor: setCursor,
        onSetOpen: setOpen,
        options,
    });

    return (
        <Popover open={disabled === true ? false : open} onOpenChange={disabled === true ? undefined : setOpen}>
            <PopoverTrigger asChild={true}>
                {/** biome-ignore lint/a11y/noStaticElementInteractions: suppressed due to migration */}
                <div
                    style={{ display: 'inline-block', pointerEvents: disabled === true ? 'none' : 'auto' }}
                    onKeyDown={disabled === true ? undefined : keyDownListener}
                >
                    {children}
                </div>
            </PopoverTrigger>
            <StyledPopoverContent align="start" sideOffset={4} onOpenAutoFocus={(e) => e.preventDefault()}>
                <Wrapper>
                    <ScrollArea orientation="vertical">
                        <List role="listbox">
                            {options.length === 0 && <Empty>{emptyMessage}</Empty>}
                            {options.map((option, index) => (
                                <DropdownItem
                                    key={option.value}
                                    checkmarkColor={checkmarkColor}
                                    disabled={option.disabled === true}
                                    icon={option.icon}
                                    isActive={cursor === index}
                                    isSelected={is.array(value) ? value.includes(option.value) : value === option.value}
                                    value={option.value}
                                    onSelect={() => handleSelect(props, option.value)}
                                >
                                    {option.label}
                                </DropdownItem>
                            ))}
                        </List>
                    </ScrollArea>
                </Wrapper>
            </StyledPopoverContent>
        </Popover>
    );
};
